// src/components/pages/allocation/Allocation.js

import { useState, useEffect, useRef } from "react"; // useRef 추가
import PortfolioInput from "./partial/PortfolioInput.js";
import TickerInput from '../../TickerInput/TickerInput.js';
import { useNavigate, useLocation } from 'react-router-dom';
import ResultChart from "./partial/ResultChart.js";
import BacktestResult from "./partial/BacktestResult.js";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import commonStyles from '../../../styles/Common.module.css';
import allocStyles from './style/Allocation.module.css';
import { useTranslation } from "react-i18next";
import NewPortfolioModal from '../portfolios/modal/NewPortfolioModal.js';
import LoginPromptToast from '../../partials/LoginPromptToast/LoginPromptToast.js';
import { useAuth } from 'react-oidc-context';
import { format } from "date-fns";
import ChallengeCta from "../../partials/ChallengeCta/ChallengeCta";

// ✅ ADDED: 포트폴리오 액션 훅 import
import { usePortfolioActions } from '../backtest/hooks/usePortfolioActions';

// URL에서 초기 상태 로드 헬퍼 함수
const getInitialStateFromURL = (search) => {
  const params = new URLSearchParams(search);
  const urlStocks = params.get('stocks')?.split(',').map(t => t.toUpperCase().trim().replace(/[^A-Z0-9.^-]/g, '')) || [];
  const isReadyForAutoRun = urlStocks.length >= 2 && params.get('optimizeType');

  const isCustomPeriod = !!params.get('startDate') && !!params.get('endDate');
  const isBacktestCustom = params.get('isBacktestCustom') === 'true';

  const parseSafe = (dateStr) => dateStr ? new Date(dateStr) : null;

  let userViews = {};
  try {
    const urlUserViews = params.get('userViews');
    if (urlUserViews) {
      userViews = JSON.parse(decodeURIComponent(urlUserViews));
    }
  } catch (e) {
    console.error('Failed to parse userViews from URL:', e);
  }

  return {
    stocks: urlStocks.length ? urlStocks : JSON.parse(localStorage.getItem("myStocks")) || [],
    optimizeType: params.get('optimizeType') || 'minVar',
    riskFreeRate: parseFloat(params.get('riskFreeRate')) || 3.6,
    allowShortSelling: params.get('allowShortSelling') === 'true',
    dataPeriod: isCustomPeriod ? null : parseInt(params.get('period'), 10) || 1,
    startDate: parseSafe(params.get('startDate')),
    endDate: parseSafe(params.get('endDate')) || new Date(),
    isCustomPeriod,
    backtestPeriod: parseInt(params.get('backtestPeriod'), 10) || 3,
    backtestStartDate: parseSafe(params.get('backtestStartDate')),
    backtestEndDate: parseSafe(params.get('backtestEndDate')) || new Date(),
    isBacktestCustomPeriod: isBacktestCustom,
    userViews, // 추가: URL에서 파싱된 userViews
    isReadyForAutoRun,
  };
};

function Allocation() {
  const { t } = useTranslation();
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const initialState = getInitialStateFromURL(location.search);

  // 상태 중앙 관리 (Date 객체 유지)
  const [stocks, setStocks] = useState(initialState.stocks);
  const [optimizeType, setOptimizeType] = useState(initialState.optimizeType);
  const [riskFreeRate, setRiskFreeRate] = useState(initialState.riskFreeRate);
  const [allowShortSelling, setAllowShortSelling] = useState(initialState.allowShortSelling);
  const [dataPeriod, setDataPeriod] = useState(initialState.dataPeriod);
  const [startDate, setStartDate] = useState(initialState.startDate);
  const [endDate, setEndDate] = useState(initialState.endDate);
  const [isCustomPeriod, setIsCustomPeriod] = useState(initialState.isCustomPeriod);
  const [userViews, setUserViews] = useState(initialState.userViews); // 추가: initialState.userViews로 초기화
  const [optimizedWeights, setOptimizedWeights] = useState({});
  const [portfolioCalculated, setPortfolioCalculated] = useState(false);
  const [backtestCalculated, setBacktestCalculated] = useState(false);
  const [backtestPeriod, setBacktestPeriod] = useState(initialState.backtestPeriod);
  const [backtestStartDate, setBacktestStartDate] = useState(initialState.backtestStartDate);
  const [backtestEndDate, setBacktestEndDate] = useState(initialState.backtestEndDate);
  const [isBacktestCustomPeriod, setIsBacktestCustomPeriod] = useState(initialState.isBacktestCustomPeriod);
  const [showOptimization, setShowOptimization] = useState(initialState.isReadyForAutoRun);
  const [showRegisterPortfolioModal, setShowRegisterPortfolioModal] = useState(false); 
  const dropdownRef = useRef(null);  // 드롭다운 외부 클릭 감지를 위한 ref

  // 추가: 초기 렌더링 플래그
  const isInitialRender = useRef(true);

  // URL 자동 실행이 시작되었는지 여부를 추적합니다.
  const [isAutoRunComplete, setIsAutoRunComplete] = useState(false);

  // =================================================================
  // ✅ ADDED: 포트폴리오 불러오기 로직 설정
  // =================================================================
  const actions = usePortfolioActions(
    auth,
    {
      setRows: (holdings) => {
        // ticker만 추출 (Allocation은 비중 최적화 페이지이므로 비중 정보는 필요 없음)
        const tickerList = holdings
          .map(h => h.ticker)
          .filter(ticker => ticker && ticker.trim() !== "");
        
        setStocks(tickerList);

        // 새로운 종목이 로드되었으므로 기존 분석 결과 초기화
        setOptimizedWeights({});
        setPortfolioCalculated(false);
        setBacktestCalculated(false);
        // setShowOptimization(true); // 포트폴리오 로드 후 입력창 바로 보여주기 (선택 사항)
      },
      setMessage: (msg) => { 
        if (msg) console.log(msg); 
      }
    },
    t,
    navigate
  );

  const handleLoadPortfolioCustom = (portfolio) => {
    actions.handleLoadPortfolio(portfolio);
  };

   // 외부 클릭 시 드롭다운 닫기 로직
  useEffect(() => {
    const handleClickOutside = (event) => {
      // dropdownRef가 가리키는 요소 밖을 클릭했을 경우에만 닫음
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        if (actions.setIsLoadModalOpen) {
          actions.setIsLoadModalOpen(false);
        }
      }
    };

    // mousedown 이벤트 리스너 등록
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      // 언마운트 시 리스너 제거
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [actions]);
  
  // =================================================================

  // 페이지 제목 설정
  useEffect(() => {
    document.title = 'QuanPort-Asset Allocation';
  }, [t]);

  // PortfolioInput 관련 상태 변경 시 결과 숨기기 (초기 렌더링 제외)
  useEffect(() => {
    if (!isInitialRender.current) {
      setPortfolioCalculated(false);
      setBacktestCalculated(false);
    }
  }, [
    stocks,
    optimizeType,
    riskFreeRate,
    allowShortSelling,
    dataPeriod,
    startDate,
    endDate,
    isCustomPeriod,
    userViews,
  ]);

  // ResultChart 관련 상태 변경 시 백테스트만 숨기기 (초기 렌더링 제외)
  useEffect(() => {
    if (!isInitialRender.current) {
      setBacktestCalculated(false);
    }
  }, [
    backtestPeriod,
    backtestStartDate,
    backtestEndDate,
    isBacktestCustomPeriod,
    optimizedWeights, // ResultChart에서 비중 조정 시
  ]);

  // 초기 렌더링 후 플래그 변경
  useEffect(() => {
    isInitialRender.current = false;
  }, []);

  // 가중치 저장
  useEffect(() => {
    if (portfolioCalculated && Object.keys(optimizedWeights).length > 0) {
      localStorage.setItem("optimizedPortfolioWeights", JSON.stringify(optimizedWeights));
    }
  }, [optimizedWeights, portfolioCalculated]);

  // URL 자동 실행 (수정: initialState 값 직접 전달)
  // URL 자동 실행 (수정: initialState 값 직접 전달)
  useEffect(() => {
      if (initialState.isReadyForAutoRun) {
          // 💡 1. handleCalculate 호출 및 불안정한 setTimeout 모두 제거
          
          // 2. ResultChart를 즉시 표시하도록 설정
          // 이 상태 변경은 initialState로 이미 설정된 props를 ResultChart에 전달하여 렌더링을 유발합니다.
          setPortfolioCalculated(true);
          
          // 3. ResultChart가 가중치를 계산 완료하면 백테스트를 실행하도록 플래그 설정
          setIsAutoRunComplete(true); 

          // 4. Result Chart 섹션으로 스크롤
          // setTimeout(() => {
          //     document.getElementById('result-chart-section')?.scrollIntoView({ behavior: "smooth" });
          // }, 100);
      }
  }, []); // 의존성 배열은 비워둡니다.


  // Optimized weights 로드 후 백테스트 자동 실행 (URL 자동 실행 시)
  useEffect(() => {
    // 1. URL로 자동 실행이 시작되었고 (isAutoRunComplete)
    // 2. 포트폴리오 계산이 완료되었으며 (portfolioCalculated)
    // 3. 그리고 최적화된 가중치가 실제로 설정되었다면 (Object.keys(optimizedWeights).length > 0)
    if (isAutoRunComplete && portfolioCalculated && Object.keys(optimizedWeights).length > 0) {
      // 백테스트 결과 표시
      setBacktestCalculated(true);
      
      // 백테스트 섹션으로 스크롤 (가시성을 위해 딜레이를 약간 줍니다)
      setTimeout(() => { 
          document.getElementById('backtest-section')?.scrollIntoView({ behavior: "smooth" });
      }, 100); 
      
      // 자동 실행 로직 완료 후 플래그 초기화
      setIsAutoRunComplete(false); 
    }
  }, [optimizedWeights, portfolioCalculated, isAutoRunComplete]);

  // 계산 핸들러 (수정: stocksParam 유효성 검사 추가)
  const handleCalculate = async (
    stocksParam, optimizeTypeParam, riskFreeRateParam, userViewsParam, allowShortSellingParam,
    dataPeriodParam, startDateParam, endDateParam, skipUrlUpdate = false
  ) => {
    // 유효성 검사 (stocksParam이 배열인지 확인)
    if (!Array.isArray(stocksParam)) {
      console.error('stocksParam is not an array:', stocksParam);
      toast.error(t("warning.invalidStocks"));
      return;
    }
    if (stocksParam.length < 2 || stocksParam.length > 20) {
      toast.error(stocksParam.length < 2 ? t("warning1moreTicker") : t("warningTooManyTicker"));
      return;
    }
    const isCustom = !!startDateParam && !!endDateParam;
    if (isCustom && (startDateParam > endDateParam)) {
      toast.error(t("warning.startDateAfterEndDate"));
      return;
    }

    // 상태 업데이트 (Date 객체 직접 설정)
    setStocks(stocksParam);
    setOptimizeType(optimizeTypeParam);
    setRiskFreeRate(riskFreeRateParam);
    setUserViews(userViewsParam);
    setAllowShortSelling(allowShortSellingParam);
    setDataPeriod(dataPeriodParam);
    setIsCustomPeriod(isCustom);
    if (isCustom) {
      setStartDate(startDateParam);
      setEndDate(endDateParam);
    } else {
      setStartDate(null);
      setEndDate(null);
    }

    setPortfolioCalculated(true); // UI 트리거 (ResultChart useEffect가 props 변경 감지)

    // URL 업데이트 (필요 시에만 문자열 변환)
    if (!skipUrlUpdate) {
      const apiStartDate = isCustom ? format(startDateParam, 'yyyy-MM-dd') : '';
      const apiEndDate = isCustom ? format(endDateParam, 'yyyy-MM-dd') : '';
      const params = new URLSearchParams({
        stocks: stocksParam.join(','), // 안전: stocksParam은 배열로 확인됨
        optimizeType: optimizeTypeParam,
        riskFreeRate: riskFreeRateParam.toString(),
        allowShortSelling: allowShortSellingParam.toString(),
        ...(dataPeriodParam && { period: dataPeriodParam.toString() }),
        ...(isCustom && { startDate: apiStartDate, endDate: apiEndDate }),
        ...(Object.keys(userViewsParam).length > 0 && { userViews: encodeURIComponent(JSON.stringify(userViewsParam)) }), // 추가: userViews URL에 포함
      });
      navigate(`?${params.toString()}`, { replace: true });
    }

    // 스크롤
    setTimeout(() => document.getElementById('result-chart-section')?.scrollIntoView({ behavior: "smooth" }), 1000);
  };

  // 백테스트 핸들러
  const handleBacktest = () => {
    if (isBacktestCustomPeriod && (!backtestStartDate || !backtestEndDate || backtestStartDate > backtestEndDate)) {
      toast.error(t("warning.selectCustomDates"));
      return;
    }
    setBacktestCalculated(true);
  };

  // 포트폴리오 등록
  const handleRegisterPortfolioClick = () => {
    if (!auth.isAuthenticated) {
      toast.error(<LoginPromptToast />);
      return;
    }
    setShowRegisterPortfolioModal(true);
  };

  // 공유 (추가: userViews를 URL에 포함)
  const handleShareResult = () => {
    const apiStartDate = isCustomPeriod ? format(startDate, 'yyyy-MM-dd') : '';
    const apiEndDate = isCustomPeriod ? format(endDate, 'yyyy-MM-dd') : '';
    const params = new URLSearchParams({
      stocks: stocks.join(','),
      optimizeType,
      riskFreeRate: riskFreeRate.toString(),
      allowShortSelling: allowShortSelling.toString(),
      ...(dataPeriod && { period: dataPeriod.toString() }),
      ...(isCustomPeriod && { startDate: apiStartDate, endDate: apiEndDate }),
      ...(Object.keys(userViews).length > 0 && { userViews: encodeURIComponent(JSON.stringify(userViews)) }), // 추가
      backtestPeriod: backtestPeriod.toString(),
      ...(isBacktestCustomPeriod && {
        backtestStartDate: format(backtestStartDate, 'yyyy-MM-dd'),
        backtestEndDate: format(backtestEndDate, 'yyyy-MM-dd'),
        isBacktestCustom: 'true',
      }),
    });
    const url = `${window.location.origin}/allocation?${params.toString()}`;
    navigator.clipboard.writeText(url);
    toast.success(t("linkCopied"));
  };

  // 예시 데이터 로드
  const loadExample = () => {
    const exampleStocks = ["AAPL.US", "MSFT.US", "GOOGL.US"];
    setStocks(exampleStocks);
    setShowOptimization(true);
    setPortfolioCalculated(false);
    toast.info(t('loadExampleDisclaimer'));
  };

  return (
    <div className={commonStyles.Page}>
      <main className={commonStyles.Container}>
        <div className={commonStyles.introContainer}>
          <p className={commonStyles.introMain}>{t('allocIntroMain')}</p>
          <p className={commonStyles.introSub}>{t('allocIntroSub')}</p>
        </div>

        <section className={`${commonStyles.card} ${allocStyles.input_section}`}>
          <h2>{t("enterticker")}</h2>
          <TickerInput
            stocks={stocks}
            onStocksChange={(newStocks) => {
              setStocks(newStocks);
              setPortfolioCalculated(false);
            }}
          />
          {stocks.length > 0 && (
            <button
              className={`${allocStyles.alloc_secondary_button} ${allocStyles.alloc_reset_stocks_button}`}
              onClick={() => setStocks([])}
            >
              {t('correlation.resetStocks')}
            </button>
          )}
        </section>

        <div className={allocStyles.alloc_intro_actions}>
          {/* ✅ ADDED: 내 포트폴리오 불러오기 버튼 및 목록 */}
          <div 
            ref={dropdownRef}
            style={{ position: 'relative', display: 'inline-block' }}>
            <button
              onClick={() => actions.fetchUserPortfolios(false)}
              className={allocStyles.alloc_secondary_button}
              style={{ marginRight: "10px" }}
              disabled={actions.isPortfoliosLoading}
              title={!auth.isAuthenticated ? t('newPortfolioModal.loginRequiredTooltip') || '로그인이 필요합니다.' : ''}
            >
              {actions.isPortfoliosLoading ? t('loading') || '로딩 중...' : t('backtest.loadMyPortfolioButton') || "내 포트폴리오 불러오기"}
            </button>

            {/* 드롭다운 목록 */}
            {actions.isLoadModalOpen && actions.userPortfolios.length > 0 && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                zIndex: 1000,
                backgroundColor: 'white',
                border: '1px solid #ccc',
                borderRadius: '4px',
                width: '250px',
                maxHeight: '300px',
                overflowY: 'auto',
                boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
                padding: '5px',
                textAlign: 'left'
              }}>
                <h4 style={{ margin: '5px 10px', fontSize: '0.9em', color: '#666' }}>{t('backtest.selectPortfolio') || '포트폴리오 선택'}</h4>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {actions.userPortfolios.map(p => (
                    <li 
                      key={p.portfolioId} 
                      onClick={() => handleLoadPortfolioCustom(p)}
                      style={{
                        padding: '8px 10px',
                        cursor: 'pointer',
                        borderBottom: '1px solid #eee',
                        fontSize: '0.9em',
                        color: '#333'
                      }}
                      onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                      onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      {p.name}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          {/* ✅ ADDED END */}

          <button className={allocStyles.alloc_secondary_button} onClick={loadExample}>
            {t('loadExample')}
          </button>
          <button
            className={allocStyles.alloc_primary_button}
            onClick={() => {
              setShowOptimization(true);
              setTimeout(() => document.getElementById('portfolio-input-section')?.scrollIntoView({ behavior: "smooth" }), 1000);
            }}
            style={{ display: showOptimization ? 'none' : 'block' }}
          >
            {t('slectAlloc')}
          </button>
        </div>

        {showOptimization && (
          <>
            <section className={commonStyles.card} id="portfolio-input-section">
              <PortfolioInput
                stocks={stocks}
                optimizeType={optimizeType}
                setOptimizeType={setOptimizeType}
                userViews={userViews}
                setUserViews={setUserViews}
                riskFreeRate={riskFreeRate}
                setRiskFreeRate={setRiskFreeRate}
                dataPeriod={dataPeriod}
                setDataPeriod={setDataPeriod}
                startDate={startDate}
                setStartDate={setStartDate}
                endDate={endDate}
                setEndDate={setEndDate}
                isCustomPeriod={isCustomPeriod}
                setIsCustomPeriod={setIsCustomPeriod}
                allowShortSelling={allowShortSelling}
                setAllowShortSelling={setAllowShortSelling}
                onCalculate={handleCalculate}
              />
            </section>

            {portfolioCalculated && (
              <div className={commonStyles.card} id="result-chart-section">
                <h2>{t("optimalallocation")}</h2>
                <ResultChart
                  stocks={stocks}
                  optimizeType={optimizeType}
                  riskFreeRate={riskFreeRate}
                  userViews={userViews}
                  onWeightsChange={setOptimizedWeights}
                  backtestPeriod={backtestPeriod}
                  setBacktestPeriod={setBacktestPeriod}
                  allowShortSelling={allowShortSelling}
                  onBacktest={handleBacktest}
                  dataPeriod={dataPeriod}
                  startDate={startDate}
                  endDate={endDate}
                  backtestStartDate={backtestStartDate}
                  setBacktestStartDate={setBacktestStartDate}
                  backtestEndDate={backtestEndDate}
                  setBacktestEndDate={setBacktestEndDate}
                  isBacktestCustomPeriod={isBacktestCustomPeriod}
                  setIsBacktestCustomPeriod={setIsBacktestCustomPeriod}
                />
                <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                  <button
                    className={allocStyles.alloc_secondary_button}
                    onClick={handleRegisterPortfolioClick}
                    style={{ flexGrow: 1 }}
                  >
                    {t("portfolios.createButton5")}
                  </button>
                  <button
                    className={allocStyles.alloc_primary_button}
                    onClick={handleShareResult}
                    style={{ flexGrow: 1 }}
                  >
                    {t("shareLink")}
                  </button>
                </div>
              </div>
            )}

            {backtestCalculated && (
              <div className={commonStyles.card} id="backtest-section">
                <h2>{t("backtestresult")}</h2>
                <BacktestResult
                  stocks={stocks}
                  period={backtestPeriod}
                  benchmark="SPY"
                  optimizedWeights={optimizedWeights}
                  customStart={isBacktestCustomPeriod ? format(backtestStartDate, 'yyyy-MM-dd') : null}
                  customEnd={isBacktestCustomPeriod ? format(backtestEndDate, 'yyyy-MM-dd') : null}
                />
              </div>
            )}
          </>
        )}
      </main>

      {backtestCalculated && (
        <ChallengeCta stocks={stocks} optimizedWeights={optimizedWeights} />
      )}

     

      <ToastContainer
        className={allocStyles.alloc_toast_container}
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick={false}
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />

      {showRegisterPortfolioModal && auth.isAuthenticated && (
        <NewPortfolioModal
          onClose={() => setShowRegisterPortfolioModal(false)}
          onCreated={() => {
            setShowRegisterPortfolioModal(false);
            toast.success(t('portfolioCreated'));
          }}
          isForcedPublic={false}
          initialHoldings={stocks.map(ticker => ({
            ticker,
            weight: Math.max(0, parseFloat(optimizedWeights[ticker]) / 100 || 0),
          }))}
          initialName="My Optimized Portfolio"
        />
      )}
    </div>
  );
}

export default Allocation;